import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, inject, Component, EventEmitter, Input, Output, ViewChild, HostListener, NgModule } from '@angular/core';
import * as i1 from '@angular/router';
import { Router } from '@angular/router';
import * as i2$1 from '@azure/msal-angular';
import { MsalService, MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalInterceptor, MsalGuard, MsalBroadcastService, MsalModule } from '@azure/msal-angular';
import { switchMap, of, catchError, map, BehaviorSubject, forkJoin, Observable, throwError, Subscription, filter, Subject, take as take$1 } from 'rxjs';
import * as i2 from '@azure/msal-browser';
import { EventType, InteractionStatus, LogLevel, PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { take, shareReplay, switchMap as switchMap$1, catchError as catchError$1, debounceTime } from 'rxjs/operators';
import * as i1$1 from '@angular/common/http';
import { HttpParams, HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import * as i1$2 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i2$2 from '@angular/forms';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import * as i3 from '@ng-bootstrap/ng-bootstrap';
import { NgbDropdown, NgbModule } from '@ng-bootstrap/ng-bootstrap';

var UserType;
(function (UserType) {
    UserType["InternalUsers"] = "users";
    UserType["AdGroups"] = "groups";
    UserType["ExternalUsers"] = "External User";
    UserType["ServicePrinciples"] = "Service Principle";
})(UserType || (UserType = {}));

var UserTypeFlag;
(function (UserTypeFlag) {
    UserTypeFlag[UserTypeFlag["None"] = 0] = "None";
    UserTypeFlag[UserTypeFlag["InternalUsers"] = 1] = "InternalUsers";
    UserTypeFlag[UserTypeFlag["AdGroups"] = 2] = "AdGroups";
    UserTypeFlag[UserTypeFlag["ExternalUsers"] = 4] = "ExternalUsers";
    UserTypeFlag[UserTypeFlag["ServicePrinciples"] = 8] = "ServicePrinciples";
    UserTypeFlag[UserTypeFlag["All"] = 15] = "All";
})(UserTypeFlag || (UserTypeFlag = {}));

const LIBRARY_NAME = 'wre-authlib';
const LIBRARY_VERSION = '16.0.4';
const MSAL_EXTERNAL_CONFIG = 'msal-external-config';
const MSAL_CONFIG_SELECTED = 'msal-config-selected';
const MSAL_REDIRECT_URL = 'msal-redirect-url';
const MSAL_LOGIN_STARTED = 'msal-login-started';

class MsalConfigGuardService {
    canActivate(router, msalService, authenticationService, authConfigService, logger, locationService) {
        logger.logVerbose('MsalConfigGuardService called');
        const configuration = msalService.instance.getConfiguration();
        const activeAccount = authenticationService.checkAndSetAccountInfo();
        if (activeAccount?.idTokenClaims?.aud === configuration.auth.clientId) {
            return true;
        }
        if (!activeAccount) {
            if (authConfigService.isInternalOnly) {
                return true;
            }
            if (localStorage.getItem(MSAL_CONFIG_SELECTED) !== 'true' ||
                localStorage.getItem(MSAL_LOGIN_STARTED) === 'true') {
                locationService.setRedirectUrl();
                return router.createUrlTree([authConfigService.preloginPageUrl]);
            }
            return true;
        }
        if (activeAccount.idTokenClaims?.aud === authConfigService.internalClientId) {
            localStorage.setItem(MSAL_EXTERNAL_CONFIG, 'false');
            locationService.reload();
            return false;
        }
        if (!authConfigService.isInternalOnly &&
            activeAccount.idTokenClaims?.aud === authConfigService.externalClientId) {
            localStorage.setItem(MSAL_EXTERNAL_CONFIG, 'true');
            locationService.reload();
            return false;
        }
        logger.logInfo('MsalConfigGuardService - Incompatible user account');
        msalService.loginRedirect({
            redirectUri: authConfigService.redirectUri,
            authority: authConfigService.msalConfig.auth.authority,
            scopes: authConfigService.scopes,
        });
        return false;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MsalConfigGuardService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MsalConfigGuardService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MsalConfigGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class MsalLoginGuardService {
    canActivate(logger) {
        logger.logVerbose('MsalLoginGuardService called');
        localStorage.removeItem(MSAL_LOGIN_STARTED);
        return true;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MsalLoginGuardService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MsalLoginGuardService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MsalLoginGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class PermissionGuardService {
    canActivate(router, route, authorisationService, authConfigService, logger) {
        logger.logVerbose('PermissionGuardService called');
        return authorisationService.checkUserHasPermissions(route.data?.['permissions']).pipe(switchMap((valid) => {
            if (!valid) {
                logger.logInfo('PermissionGuardService - User not authorised');
                router.navigate([route.data?.['redirectUrl'] ?? authConfigService.unauthorisedPageUrl]);
            }
            return of(valid);
        }), catchError((error) => {
            logger.logError('PermissionGuardService error:', error);
            router.navigate([route.data?.['redirectUrl'] ?? authConfigService.unauthorisedPageUrl]);
            return of(false);
        }));
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PermissionGuardService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PermissionGuardService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PermissionGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class RoleGuardService {
    canActivate(router, route, authorisationService, authConfigService, logger) {
        logger.logVerbose('RoleGuardService called');
        return authorisationService.checkUserHasRoles(route.data?.['roles']).pipe(switchMap((valid) => {
            if (!valid) {
                logger.logInfo('RoleGuardService - User not authorised');
                router.navigate([route.data?.['redirectUrl'] ?? authConfigService.unauthorisedPageUrl]);
            }
            return of(valid);
        }), catchError((error) => {
            logger.logError('RoleGuardService error:', error);
            router.navigate([route.data?.['redirectUrl'] ?? authConfigService.unauthorisedPageUrl]);
            return of(false);
        }));
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RoleGuardService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RoleGuardService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RoleGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class UnauthenticatedGuardService {
    canActivate(router, msalService, authConfigService, logger) {
        logger.logVerbose('UnauthenticatedGuard called');
        const unauthenticated = msalService.instance.getAllAccounts().length === 0;
        if (!unauthenticated) {
            logger.logInfo('UnauthenticatedGuard - Redirect as user already authenticated');
            router.navigate([authConfigService.homePageUrl]);
        }
        return unauthenticated;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UnauthenticatedGuardService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UnauthenticatedGuardService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UnauthenticatedGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const INTERNAL_AUTH_CONFIG = new InjectionToken('INTERNAL_AUTH_CONFIG');
const EXTERNAL_AUTH_CONFIG = new InjectionToken('EXTERNAL_AUTH_CONFIG');
const LOGGING_ENABLED = new InjectionToken('LOGGING_ENABLED');
const LOG_LEVEL = new InjectionToken('LOG_LEVEL');

class AuthConfigService {
    constructor(internalConfig, externalConfig, logger, logLevel) {
        this.internalConfig = internalConfig;
        this.externalConfig = externalConfig;
        this.logger = logger;
        this.logLevel = logLevel;
        this.external = false;
        this.internalOnly = false;
        this.external = !!externalConfig && localStorage.getItem(MSAL_EXTERNAL_CONFIG) === 'true';
        this.internalOnly = !externalConfig;
        this.msalInternalConfig = this.createConfiguration(this.internalConfig);
        this.msalExternalConfig = this.createConfiguration(this.externalConfig);
        this.internalScopes = this.applyCustomScopes(this.internalConfig);
        this.externalScopes = this.applyCustomScopes(this.externalConfig);
        this.logger.logInfo(`AuthConfig - external=${this.external}`);
        this.logger.logInfo(`AuthConfig - internalOnly=${this.internalOnly}`);
    }
    get isExternal() {
        return this.external;
    }
    get isInternalOnly() {
        return this.internalOnly;
    }
    get msalConfig() {
        return this.external ? this.msalExternalConfig : this.msalInternalConfig;
    }
    get applicationName() {
        return this.external
            ? this.externalConfig?.applicationName
            : this.internalConfig.applicationName;
    }
    get internalClientId() {
        return this.internalConfig.auth.clientId;
    }
    get externalClientId() {
        return this.externalConfig?.auth?.clientId;
    }
    get redirectUri() {
        return this.external
            ? this.externalConfig?.auth?.redirectUri
            : this.internalConfig.auth.redirectUri;
    }
    get protectedResourceMap() {
        return ((this.external
            ? this.externalConfig?.protectedResourceMap
            : this.internalConfig.protectedResourceMap) ??
            new Map([['https://graph.microsoft.com/v1.0/me', ['user.read']]]));
    }
    get authorisationUrl() {
        return this.appendBackslash((this.external
            ? this.externalConfig?.authorisationUrl
            : this.internalConfig.authorisationUrl) ?? '');
    }
    get authenticationCacheSeconds() {
        return ((this.external
            ? this.externalConfig?.authenticationCacheSeconds
            : this.internalConfig.authenticationCacheSeconds) ?? 60);
    }
    get cacheTimeInSeconds() {
        return ((this.external
            ? this.externalConfig?.cacheTimeInSeconds
            : this.internalConfig.cacheTimeInSeconds) ?? 10);
    }
    get preloginPageUrl() {
        return ((this.external
            ? this.externalConfig?.preloginPageUrl
            : this.internalConfig.preloginPageUrl) ?? '/');
    }
    get homePageUrl() {
        return ((this.external ? this.externalConfig?.homePageUrl : this.internalConfig.homePageUrl) ??
            '/dashboard');
    }
    get unauthorisedPageUrl() {
        return ((this.external
            ? this.externalConfig?.unauthorisedPageUrl
            : this.internalConfig.unauthorisedPageUrl) ?? '/unauthorized');
    }
    get scopes() {
        return this.external ? this.externalScopes : this.internalScopes;
    }
    get resetPasswordAuthority() {
        return this.external
            ? this.externalConfig?.resetPasswordAuthority
            : this.internalConfig.resetPasswordAuthority;
    }
    createConfiguration(config) {
        return {
            auth: config?.auth,
            cache: config?.cache,
            system: {
                loggerOptions: {
                    loggerCallback: (_logLevel, message) => {
                        this.logger.logRaw(message);
                    },
                    logLevel: this.logLevel,
                    piiLoggingEnabled: false,
                },
            },
        };
    }
    applyCustomScopes(config) {
        return [
            ...(config?.scopes ?? ['offline_access', 'user.read']),
            ...(config?.customScopes ?? []),
        ].filter((value, index, self) => self.indexOf(value) === index);
    }
    appendBackslash(url) {
        return url.endsWith('/') ? url : `${url}/`;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthConfigService, deps: [{ token: INTERNAL_AUTH_CONFIG }, { token: EXTERNAL_AUTH_CONFIG }, { token: LoggerService }, { token: LOG_LEVEL }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthConfigService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [INTERNAL_AUTH_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [EXTERNAL_AUTH_CONFIG]
                }] }, { type: LoggerService }, { type: i2.LogLevel, decorators: [{
                    type: Inject,
                    args: [LOG_LEVEL]
                }] }]; } });

class AuthenticationService {
    constructor(msalService, authConfigService, cache) {
        this.msalService = msalService;
        this.authConfigService = authConfigService;
        this.cache = cache;
    }
    checkAndSetAccountInfo() {
        let activeAccount = this.msalService.instance.getActiveAccount();
        if (!activeAccount) {
            const accounts = this.msalService.instance.getAllAccounts();
            if (accounts.length > 0) {
                activeAccount =
                    accounts.find((account) => account?.idTokenClaims?.aud === this.authConfigService.internalClientId) ?? accounts[0];
                this.msalService.instance.setActiveAccount(activeAccount);
            }
        }
        return activeAccount;
    }
    // NOTE: Retrieves token for scopes used in time survey
    getAuthenticationToken(refreshScopes) {
        const scopes = [].concat(refreshScopes);
        const cacheKey = `token${scopes.join(',')}`;
        return this.cache.getObservable(cacheKey, this.acquireToken(scopes), 5).pipe(switchMap((token) => of(token)), catchError(() => {
            this.msalService.loginRedirect({ scopes });
            return of(null);
        }));
    }
    logout() {
        this.msalService.logout();
    }
    acquireToken(scopes) {
        const account = this.checkAndSetAccountInfo();
        if (account) {
            return this.msalService
                .acquireTokenSilent({ scopes, account })
                .pipe(map((authenticationResult) => authenticationResult.idToken));
        }
        else {
            this.msalService.loginRedirect({ scopes });
            return of(null);
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationService, deps: [{ token: i2$1.MsalService }, { token: AuthConfigService }, { token: CacheService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i2$1.MsalService }, { type: AuthConfigService }, { type: CacheService }]; } });

class UserProfile {
    get friendlyName() {
        return this.getFriendlyName(this.name);
    }
    getFriendlyName(name) {
        let friendlyName = name?.replace('(AJ Gallagher)', '');
        if (name?.includes(',')) {
            const splitted = name.split(',');
            friendlyName = splitted[splitted.length - 1];
            for (let i = 0; i < splitted.length - 1; i++) {
                friendlyName += ' ' + splitted[i];
            }
        }
        return friendlyName?.trim();
    }
}

class AuthorisationService {
    constructor(authConfigService, httpClientService) {
        this.authConfigService = authConfigService;
        this.httpClientService = httpClientService;
        this.user$ = new BehaviorSubject(null);
    }
    getUserDetails() {
        return this.httpClientService.getUserDetails();
    }
    getUserGroups() {
        return this.httpClientService.getUserGroups();
    }
    getUser() {
        return forkJoin([
            this.httpClientService.getUserApplicationRoles(),
            this.httpClientService.getUserDetails(),
            this.httpClientService.getUserGroups(),
            this.httpClientService
                .getUserPhoto()
                .pipe(switchMap((blob) => this.createImageFromBlob(blob))),
        ]).pipe(map(([applicationRoles, userDetail, userGroups, userPhoto]) => {
            const user = new UserProfile();
            user.roles = applicationRoles.map((r) => r.roleName);
            user.groups = userGroups;
            user.photo = userPhoto;
            user.email = userDetail.email;
            user.principleName = userDetail.principleName;
            user.name = userDetail.displayName?.replace('(AJ Gallagher)', '');
            user.firstName = userDetail.firstName;
            user.lastName = userDetail.lastName;
            user.id = userDetail.employeeId;
            user.isExternal = this.authConfigService.isExternal;
            user.refID = userDetail.refId;
            this.user$.next(user);
            return user;
        }));
    }
    getUserId() {
        return this.user$.value ? of(this.user$.value.id) : this.getUser().pipe(map((user) => user.id));
    }
    getUserName() {
        return this.user$.value
            ? of(this.user$.value.name)
            : this.getUser().pipe(map((user) => user.name));
    }
    getUserEmail() {
        return this.user$.value
            ? of(this.user$.value.email)
            : this.getUser().pipe(map((user) => user.email));
    }
    isExternalUser() {
        return this.user$.value
            ? of(this.user$.value.isExternal)
            : this.getUser().pipe(map((user) => user.isExternal));
    }
    getUsersStartsWith(userNameStartsWith) {
        return this.httpClientService.getUsersStartsWith(userNameStartsWith);
    }
    getUsersDetailsStartsWith(applicationName, type, startsWith) {
        return this.httpClientService.getUsersDetailsStartsWith(applicationName, type, startsWith);
    }
    getUserGroupsStartsWith(groupNameStartsWith) {
        return this.httpClientService.getUserGroupsStartsWith(groupNameStartsWith);
    }
    getApplicationRoles(filterText, applicationName) {
        return this.httpClientService.getApplicationRoles(filterText, applicationName);
    }
    getUserApplicationRoles() {
        return this.httpClientService.getUserApplicationRoles();
    }
    checkUserHasRoles(roles) {
        return this.getUserApplicationRoles().pipe(map((userRoles) => !!(userRoles ?? []).find((userRole) => (roles ?? []).includes(userRole.roleName))));
    }
    getUserApplicationRolesWithPermissions() {
        return this.httpClientService.getUserApplicationRoles().pipe(map((userRoles) => (userRoles ?? []).reduce((permissionList, role) => {
            permissionList.push(role.roleName);
            (role.permissionGroups ?? []).forEach((pg) => {
                permissionList = permissionList.concat((pg.permissions ?? []).map((p) => p.name));
            });
            return permissionList;
        }, [])));
    }
    checkUserHasPermissions(permissions) {
        return this.getUserApplicationRolesWithPermissions().pipe(map((permissionList) => !!permissionList.find((permission) => (permissions ?? []).includes(permission))));
    }
    hasUserAccessToGroup(groupName) {
        return this.getUserGroups().pipe(map((userGroups) => !!userGroups
            .toLowerCase()
            .split(',')
            .find((group) => group === groupName.trim().toLocaleLowerCase())));
    }
    createImageFromBlob(blob) {
        return new Observable((observer) => {
            const fileReader = new FileReader();
            fileReader.onerror = (error) => observer.error(error);
            fileReader.onabort = (error) => observer.error(error);
            fileReader.onload = () => observer.next(fileReader.result);
            fileReader.onloadend = () => observer.complete();
            fileReader.readAsDataURL(blob);
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorisationService, deps: [{ token: AuthConfigService }, { token: HttpClientService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorisationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorisationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: AuthConfigService }, { type: HttpClientService }]; } });

class CacheService {
    constructor() {
        this.cachedList = [];
    }
    getObservable(key, obs, cacheDuration) {
        const item = this.getCacheItem(key);
        if (!item.cachedObservable$) {
            item.cachedObservable$ = obs.pipe(take(1), shareReplay(1), switchMap$1((response) => {
                const expireAfter = new Date();
                expireAfter.setSeconds(expireAfter.getSeconds() + cacheDuration);
                item.refreshAfter = expireAfter;
                return of(response);
            }), catchError$1((error) => {
                item.refreshAfter = undefined;
                return throwError(() => error);
            }));
        }
        return item.cachedObservable$;
    }
    getCacheItem(key) {
        const item = this.cachedList.find((element) => element.key === key);
        if (!item) {
            const newItem = { key };
            this.cachedList.push(newItem);
            return newItem;
        }
        if (!item.refreshAfter || item.refreshAfter < new Date()) {
            item.cachedObservable$ = undefined;
        }
        return item;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CacheService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CacheService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CacheService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class EventService {
    constructor(router, msalService, msalBroadcastService, authenticationService, authConfigService, logger) {
        this.router = router;
        this.msalService = msalService;
        this.msalBroadcastService = msalBroadcastService;
        this.authenticationService = authenticationService;
        this.authConfigService = authConfigService;
        this.logger = logger;
        this.subscriptions = new Subscription();
    }
    initialize() {
        this.msalService.instance.enableAccountStorageEvents();
        this.subscriptions.add(this.msalBroadcastService.msalSubject$
            .pipe(filter((eventMessage) => eventMessage.eventType === EventType.ACCOUNT_ADDED ||
            eventMessage.eventType === EventType.ACCOUNT_REMOVED))
            .subscribe((eventMessage) => {
            this.logger.logVerbose(`EventService - eventType=${eventMessage.eventType}`);
            if (this.msalService.instance.getAllAccounts().length === 0) {
                this.logger.logInfo('EventService - no account found');
                this.router.navigate([this.authConfigService.preloginPageUrl]);
            }
        }));
        this.subscriptions.add(this.msalBroadcastService.msalSubject$
            .pipe(filter((eventMessage) => eventMessage.eventType === EventType.LOGIN_START))
            .subscribe((eventMessage) => {
            this.logger.logVerbose(`EventService - eventType=${eventMessage.eventType}`);
            if (this.authConfigService.isInternalOnly) {
                return;
            }
            localStorage.setItem(MSAL_LOGIN_STARTED, 'true');
        }));
        this.subscriptions.add(this.msalBroadcastService.msalSubject$
            .pipe(filter((eventMessage) => eventMessage.eventType === EventType.LOGIN_SUCCESS))
            .subscribe((eventMessage) => {
            this.logger.logVerbose(`EventService - eventType=${eventMessage.eventType}`);
            localStorage.removeItem(MSAL_REDIRECT_URL);
            localStorage.removeItem(MSAL_LOGIN_STARTED);
            localStorage.removeItem(MSAL_CONFIG_SELECTED);
        }));
        this.subscriptions.add(this.msalBroadcastService.inProgress$
            .pipe(filter((status) => status === InteractionStatus.None))
            .subscribe(() => {
            const account = this.authenticationService.checkAndSetAccountInfo();
            this.logger.logInfo('EventService - assigned account:', account);
        }));
        this.subscriptions.add(this.msalBroadcastService.msalSubject$
            .pipe(filter((eventMessage) => eventMessage.eventType === EventType.LOGIN_FAILURE ||
            eventMessage.eventType === EventType.ACQUIRE_TOKEN_FAILURE))
            .subscribe((eventMessage) => {
            this.logger.logVerbose(`EventService - eventType=${eventMessage.eventType}`);
            // Checking for the forgot password error. Learn more about B2C error codes at
            // https://learn.microsoft.com/azure/active-directory-b2c/error-codes
            if ((eventMessage?.error?.message ?? '').indexOf('AADB2C90118') > -1) {
                this.msalService.loginRedirect({
                    redirectUri: this.authConfigService.redirectUri,
                    authority: this.authConfigService.resetPasswordAuthority,
                    scopes: this.authConfigService.scopes,
                });
            }
        }));
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EventService, deps: [{ token: i1.Router }, { token: i2$1.MsalService }, { token: i2$1.MsalBroadcastService }, { token: AuthenticationService }, { token: AuthConfigService }, { token: LoggerService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EventService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EventService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i2$1.MsalService }, { type: i2$1.MsalBroadcastService }, { type: AuthenticationService }, { type: AuthConfigService }, { type: LoggerService }]; } });

class HttpClientService {
    // private graphMeUrl = 'https://graph.microsoft.com/v1.0/me/';
    constructor(httpClient, cache, authConfigService) {
        this.httpClient = httpClient;
        this.cache = cache;
        this.authConfigService = authConfigService;
    }
    getApplicationRoles(filterText, applicationName) {
        let params = new HttpParams();
        params = params.append('includeDetails', 'false');
        params = params.append('activeOnly', 'true');
        params = params.append('filter', filterText);
        const url = `${this.authConfigService.authorisationUrl}applications/${applicationName ?? this.authConfigService.applicationName}/roles`;
        const cacheKey = `${url}?filter=${filterText.replace(' ', '')}`;
        return this.cache.getObservable(cacheKey, this.httpClient.get(url, { responseType: 'json', params }), this.authConfigService.authenticationCacheSeconds);
    }
    getUserApplicationRoles() {
        const url = `${this.authConfigService.authorisationUrl}userroles`;
        return this.cache.getObservable(url, this.httpClient.get(url, { responseType: 'json' }), this.authConfigService.cacheTimeInSeconds);
    }
    getUserDetails() {
        const url = `${this.authConfigService.authorisationUrl}users/userdetails`;
        return this.cache.getObservable(url, this.httpClient.get(url, { responseType: 'json' }), this.authConfigService.authenticationCacheSeconds);
    }
    getUserGroups() {
        const url = `${this.authConfigService.authorisationUrl}userroles/groups`;
        return this.cache.getObservable(url, this.httpClient
            .get(url, { responseType: 'json' })
            .pipe(map((groupList) => groupList.map((group) => group.displayName).join(','))), this.authConfigService.authenticationCacheSeconds);
    }
    getUserPhoto() {
        // TODO: Fix endpoint to user photo - graph.microsoft.com is not currently working, so the default user jpg is returned in the meantime!
        // const url = `${this.graphMeUrl}photo/$value`;
        // return this.httpClient.get(url, { responseType: 'blob' }).pipe(
        //   switchMap((blob) => of(blob)),
        //   catchError(() => this.httpClient.get('assets/images/user.jpg', { responseType: 'blob' })),
        // );
        return this.httpClient.get('assets/images/user.jpg', { responseType: 'blob' });
    }
    getUsersStartsWith(userNameStartsWith) {
        const url = `${this.authConfigService.authorisationUrl}applications/users?displayNameStartsWith=${userNameStartsWith.replace("'", "''")}`;
        return this.cache.getObservable(url, this.httpClient.get(url, { responseType: 'json' }), this.authConfigService.authenticationCacheSeconds);
    }
    getUsersDetailsStartsWith(applicationName, type, startsWith) {
        const url = `${this.authConfigService.authorisationUrl}users/${applicationName}?type=${type}&startsWith=${startsWith.replace("'", "''")}`;
        return this.cache.getObservable(url, this.httpClient.get(url, { responseType: 'json' }), this.authConfigService.authenticationCacheSeconds);
    }
    getUserGroupsStartsWith(groupNameStartsWith) {
        const url = `${this.authConfigService.authorisationUrl}applications/groups?startswith=${groupNameStartsWith.replace("'", "''")}`;
        return this.cache.getObservable(url, this.httpClient.get(url, { responseType: 'json' }), this.authConfigService.authenticationCacheSeconds);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HttpClientService, deps: [{ token: i1$1.HttpClient }, { token: CacheService }, { token: AuthConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HttpClientService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HttpClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: CacheService }, { type: AuthConfigService }]; } });

class LocationService {
    constructor(authConfigService, document) {
        this.authConfigService = authConfigService;
        this.document = document;
    }
    setRedirectUrl() {
        localStorage.setItem(MSAL_REDIRECT_URL, this.document.defaultView?.location.href ?? this.authConfigService.homePageUrl);
    }
    reload() {
        this.document.defaultView?.location.reload();
    }
    redirect(redirectUrl) {
        if (this.document.defaultView) {
            this.document.defaultView.location.href = redirectUrl;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LocationService, deps: [{ token: AuthConfigService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LocationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LocationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: AuthConfigService }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

/* eslint-disable no-console */
class LoggerService {
    constructor(loggingEnabled, logLevel) {
        this.loggingEnabled = loggingEnabled;
        this.logLevel = logLevel;
        this.infoLevels = [LogLevel.Verbose, LogLevel.Info, LogLevel.Trace];
        this.warnLevels = [LogLevel.Verbose, LogLevel.Info, LogLevel.Warning, LogLevel.Trace];
    }
    logVerbose(message) {
        if (this.loggingEnabled && this.logLevel === LogLevel.Verbose) {
            console.log(this.createMessage('Verbose', message));
        }
    }
    logInfo(message, info) {
        if (this.loggingEnabled && this.infoLevels.includes(this.logLevel)) {
            if (info) {
                console.info(this.createMessage('Info', message), info);
            }
            else {
                console.info(this.createMessage('Info', message));
            }
        }
    }
    logWarn(message, warn) {
        if (this.loggingEnabled && this.warnLevels.includes(this.logLevel)) {
            if (warn) {
                console.warn(this.createMessage('Warning', message), warn);
            }
            else {
                console.warn(this.createMessage('Warning', message));
            }
        }
    }
    logError(message, error) {
        if (this.loggingEnabled) {
            if (error) {
                console.error(this.createMessage('Error', message), error);
            }
            else {
                console.error(this.createMessage('Error', message));
            }
        }
    }
    logRaw(message) {
        if (this.loggingEnabled) {
            console.log(message);
        }
    }
    createMessage(level, message) {
        return `[${new Date().toUTCString()}] : ${LIBRARY_NAME}@${LIBRARY_VERSION} : ${level} - ${message}`;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LoggerService, deps: [{ token: LOGGING_ENABLED }, { token: LOG_LEVEL }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LoggerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: LoggerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGING_ENABLED]
                }] }, { type: i2.LogLevel, decorators: [{
                    type: Inject,
                    args: [LOG_LEVEL]
                }] }]; } });

const UnauthenticatedGuard = () => inject(UnauthenticatedGuardService).canActivate(inject(Router), inject(MsalService), inject(AuthConfigService), inject(LoggerService));
const PermissionGuard = (route) => inject(PermissionGuardService).canActivate(inject(Router), route, inject(AuthorisationService), inject(AuthConfigService), inject(LoggerService));
const RoleGuard = (route) => inject(RoleGuardService).canActivate(inject(Router), route, inject(AuthorisationService), inject(AuthConfigService), inject(LoggerService));
const MsalConfigGuard = () => inject(MsalConfigGuardService).canActivate(inject(Router), inject(MsalService), inject(AuthenticationService), inject(AuthConfigService), inject(LoggerService), inject(LocationService));
const MsalLoginGuard = () => inject(MsalLoginGuardService).canActivate(inject(LoggerService));

class PreLoginComponent {
    constructor(authConfigService, locationService) {
        this.authConfigService = authConfigService;
        this.locationService = locationService;
        this.redirectUrl = '';
    }
    ngOnInit() {
        this.redirectUrl =
            localStorage.getItem(MSAL_REDIRECT_URL) ?? this.authConfigService.homePageUrl;
        localStorage.removeItem(MSAL_CONFIG_SELECTED);
        localStorage.removeItem(MSAL_LOGIN_STARTED);
        localStorage.removeItem(MSAL_REDIRECT_URL);
        localStorage.removeItem(MSAL_EXTERNAL_CONFIG);
    }
    navigateToInternalUserLoginPage() {
        this.loginRedirect(false);
    }
    navigateToExternalUserLoginPage() {
        this.loginRedirect(true);
    }
    loginRedirect(external) {
        sessionStorage.clear();
        localStorage.setItem(MSAL_CONFIG_SELECTED, 'true');
        localStorage.setItem(MSAL_EXTERNAL_CONFIG, external ? 'true' : 'false');
        this.locationService.redirect(this.redirectUrl);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PreLoginComponent, deps: [{ token: AuthConfigService }, { token: LocationService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: PreLoginComponent, selector: "ng-component", ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PreLoginComponent, decorators: [{
            type: Component,
            args: [{
                    template: '',
                }]
        }], ctorParameters: function () { return [{ type: AuthConfigService }, { type: LocationService }]; } });

class UserLookupComponent {
    constructor() {
        this.isMultiSelect = true;
        this.displayBadge = true;
        this.control = new FormControl();
        this.inputId = 'userLookupId';
        this.placeholderValue = 'Search user...';
        this.isDisabled = false;
        this.isHorizontal = false;
        this.loading = true;
        this.list = [];
        this.readOnly = false;
        this.emptyListText = 'The list Empty/ReadOnly';
        this.noSearchResultText = 'No user found';
        this.searchEvent = new EventEmitter();
        this.dropdownKey = '';
        this.searchTerm = '';
        this.subject = new Subject();
        this.existingUsers = [];
    }
    onKeydownHandler(event) {
        if (event.code === 'Tab' && this.dropdown?.isOpen()) {
            this.dropdown.close();
        }
    }
    get name() {
        return this.inputName ?? this.inputId;
    }
    get inputValue() {
        return this.isMultiSelect
            ? this.searchTerm
            : this.control.value
                ? this.control.value.displayName
                : '';
    }
    isNewUser(user) {
        return !this.existingUsers.includes(user);
    }
    ngOnInit() {
        this.existingUsers = this.isMultiSelect && this.control.value ? [...this.control.value] : [];
        this.dropdownKey = `${this.inputId}-userList`;
        this.subject.pipe(debounceTime(200)).subscribe(() => {
            this.searchUsers();
        });
    }
    // search for users
    searchUsers() {
        const searchTerm = this.searchTerm?.trim();
        if (searchTerm?.length > 2) {
            this.searchEvent.emit(searchTerm);
            this.dropdown?.open();
        }
    }
    // Add to user lookup list
    selectUser(lookupUser) {
        if (this.isMultiSelect) {
            const currentValue = this.control.value ?? [];
            if (currentValue.filter((v) => v.id === lookupUser.id).length === 0) {
                currentValue.push(lookupUser);
                this.control.setValue(currentValue);
                this.control.markAsDirty();
            }
            this.searchTerm = '';
            document.getElementById(this.inputId)?.focus();
        }
        else {
            this.control.setValue(lookupUser);
            this.control.markAsDirty();
        }
        this.dropdown?.close();
    }
    // remove from user lookup list
    removeUser(user) {
        if (user !== undefined) {
            const currentControlValue = this.control.value;
            this.control.setValue(currentControlValue.filter((v) => v.id !== user.id));
            this.control.markAsDirty();
        }
    }
    onInput(event) {
        this.searchTerm = event.target.value;
    }
    // shared accessibility functions
    onBlur() {
        if (this.isMultiSelect) {
            if (this.searchTerm.length > 2 && this.dropdown?.isOpen() && this.list.length === 0) {
                this.dropdown.close();
            }
            this.searchTerm = '';
            this.control.markAllAsTouched();
        }
    }
    onKeyUp(event) {
        switch (event.key) {
            case 'Escape': {
                this.dropdown?.close();
                break;
            }
            case 'ArrowDown': {
                this.selectItemInDropdownList(event.key);
                break;
            }
            case 'ArrowUp': {
                this.selectItemInDropdownList(event.key);
                break;
            }
            case 'Enter': {
                this.selectItemInDropdownList(event.key);
                break;
            }
            default: {
                this.subject.next(null);
                this.dropdown?.close();
                break;
            }
        }
    }
    selectItemInDropdownList(eventKey) {
        if (!this.loading) {
            const list = document.getElementById(this.dropdownKey);
            const selected = list?.querySelector('.selected');
            const element = document.getElementById(this.inputId);
            const scrollHeight = element === null ? 0 : element.getBoundingClientRect().height;
            if (selected) {
                switch (eventKey) {
                    case 'ArrowUp': {
                        const prevElement = selected.previousElementSibling;
                        if (prevElement) {
                            selected.classList.remove('selected');
                            prevElement.classList.add('selected');
                            list?.scrollTo({ top: (list.scrollTop -= scrollHeight), behavior: 'smooth' });
                        }
                        break;
                    }
                    case 'ArrowDown': {
                        const nextElement = selected.nextElementSibling;
                        if (nextElement) {
                            selected.classList.remove('selected');
                            nextElement.classList.add('selected');
                            list?.scrollTo({ top: (list.scrollTop += scrollHeight), behavior: 'smooth' });
                        }
                        break;
                    }
                    case 'Enter': {
                        selected.click();
                    }
                }
            }
            else {
                const subjectToSelect = list?.querySelector('a');
                if (subjectToSelect) {
                    subjectToSelect.classList.add('selected');
                }
            }
        }
    }
    // single user select accessibility
    onFocus() {
        if (this.isMultiSelect) {
            return;
        }
        this.searchTerm = this.inputValue;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UserLookupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: UserLookupComponent, selector: "wtw-user-lookup", inputs: { isMultiSelect: "isMultiSelect", displayBadge: "displayBadge", control: "control", inputId: "inputId", placeholderValue: "placeholderValue", isDisabled: "isDisabled", isInvalid: "isInvalid", labelText: "labelText", inputTabIndex: "inputTabIndex", inputName: "inputName", isHorizontal: "isHorizontal", loading: "loading", list: "list", readOnly: "readOnly", emptyListText: "emptyListText", noSearchResultText: "noSearchResultText" }, outputs: { searchEvent: "searchEvent" }, host: { listeners: { "keydown": "onKeydownHandler($event)" } }, viewQueries: [{ propertyName: "dropdown", first: true, predicate: NgbDropdown, descendants: true }], ngImport: i0, template: "<div ngbDropdown container=\"body\" class=\"dropdown flex-sm-column flex-lg-column\">\r\n  <label *ngIf=\"labelText\" [for]=\"inputId\" class=\"form-control-label\">{{\r\n    labelText | titlecase\r\n  }}</label>\r\n  <div class=\"d-flex\" [ngClass]=\"{ 'flex-row': isHorizontal, 'flex-column': !isHorizontal }\">\r\n    <div class=\"d-flex flex-column\" *ngIf=\"!readOnly\">\r\n      <input\r\n        type=\"text\"\r\n        class=\"form-control dropdown-toggle dropdown\"\r\n        [class.w-100]=\"!isHorizontal\"\r\n        [class.custom-invalid]=\"isInvalid && control.touched\"\r\n        (blur)=\"onBlur()\"\r\n        (focus)=\"onFocus()\"\r\n        data-boundary=\"viewport\"\r\n        [disabled]=\"isDisabled\"\r\n        [id]=\"inputId\"\r\n        [name]=\"name\"\r\n        (input)=\"onInput($event)\"\r\n        [ngModel]=\"inputValue\"\r\n        (keyup)=\"onKeyUp($event)\"\r\n        [placeholder]=\"placeholderValue\"\r\n        [tabindex]=\"inputTabIndex\"\r\n        autocomplete=\"off\" />\r\n\r\n      <!--    The actual form field is hidden so that we can send the LookupUser object rather than just a string-->\r\n      <span ngbDropdownToggle style=\"visibility: hidden; display: block\"></span>\r\n      <input hidden [formControl]=\"control\" />\r\n\r\n      <div id=\"{{ inputId }}-userList\" ngbDropdownMenu class=\"dropdown-menu\">\r\n        <a\r\n          class=\"dropdown-item\"\r\n          *ngFor=\"let user of list; let first = first\"\r\n          ngbDropdownItem\r\n          (click)=\"selectUser(user)\"\r\n          [class.border-0]=\"first\"\r\n          >{{ user.displayName }} <span *ngIf=\"user.mail\">&nbsp;- {{ user.mail }}</span></a\r\n        >\r\n        <a\r\n          class=\"dropdown-item border-0 list-no-search\"\r\n          *ngIf=\"!loading && list?.length === 0 && (this.searchTerm?.length ?? 0) > 2\"\r\n          ngbDropdownItem>\r\n          {{ noSearchResultText }} for search term : <b>&nbsp; \"{{ this.searchTerm }}\"</b>\r\n        </a>\r\n        <a\r\n          class=\"dropdown-item border-0 list-loader\"\r\n          *ngIf=\"loading\"\r\n          ngbDropdownItem\r\n          style=\"pointer-events: none\">\r\n          <div class=\"d-flex justify-content-center align-items-center w-100\">\r\n            <div class=\"loading\"></div>\r\n          </div>\r\n        </a>\r\n      </div>\r\n    </div>\r\n\r\n    <div *ngIf=\"isMultiSelect && displayBadge\">\r\n      <ng-container\r\n        *ngIf=\"\r\n          readOnly && control.value.length === 0;\r\n          then readOnlyBlock;\r\n          else selectedUsersBlock\r\n        \"></ng-container>\r\n      <ng-template #readOnlyBlock>\r\n        <div class=\"empty-list-text\">\r\n          {{ emptyListText }}\r\n        </div>\r\n      </ng-template>\r\n      <ng-template #selectedUsersBlock>\r\n        <div\r\n          class=\"filters-selected d-sm-block d-lg-inline-block mt-sm-0\"\r\n          [ngClass]=\"{ 'mt-lg-3 py-2': !isHorizontal, 'ms-lg-3 px-2': isHorizontal }\">\r\n          <button\r\n            type=\"button\"\r\n            [disabled]=\"isDisabled || readOnly\"\r\n            (click)=\"removeUser(v)\"\r\n            *ngFor=\"let v of control.value\"\r\n            class=\"badge btn me-2 mb-2\"\r\n            ngClass=\"{{\r\n              readOnly ? 'btn-readonly' : isNewUser(v) ? 'btn-newly-added' : 'btn-already-exists'\r\n            }}\">\r\n            <span>{{ v.displayName }}</span>\r\n            <span class=\"ps-3\" *ngIf=\"!readOnly\">\u00D7</span>\r\n          </button>\r\n        </div>\r\n      </ng-template>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [".dropdown,.dropup{display:flex;flex-direction:row}.dropdown span.dropdown-toggle,.dropup span.dropdown-toggle{height:0;width:0;border:none;padding:0}.form-control{outline:none;box-shadow:0 1px 2px #0000000d;position:relative;background:#fff;border:1px solid #d8dce6;border-radius:6px;display:flex;transition:border .2s ease-in-out,background .2s ease-in-out;width:100%;font-size:.875rem;color:#3e3f42;padding:3px 10px;min-width:300px}.form-control:disabled{background:#eee;border-color:#d1d1d1;color:#c1c1c1}.form-control-label{color:#4f7ca0;font-size:.75rem;font-weight:500;letter-spacing:1px;text-transform:uppercase}.dropdown-menu{position:absolute;z-index:500;background:#fff;border:1px solid #d8dce6!important;box-shadow:0 2px 4px #0000001a;border-radius:6px;list-style:none;min-width:140px;padding:1px;margin:0;top:40px;left:0;min-height:40px;max-height:200px;overflow:auto;max-width:600px}.dropdown-menu a.dropdown-item{display:flex;align-items:center;font-size:.875rem;border-radius:0;padding:7px 15px;border-top:1px solid #fff;color:gray;cursor:pointer}.dropdown-menu a.dropdown-item:last-child{border-bottom:none}.dropdown-menu a.dropdown-item:hover,.dropdown-menu a.dropdown-item:focus,.dropdown-menu a.dropdown-item.selected{background:#f4f4fb;outline:none}.filters-selected{display:inline-block}.filters-selected .badge{letter-spacing:1px;text-transform:uppercase;font-size:10px;font-weight:500;border-radius:4px;padding:7px 7px 7px 8px;color:#fff}.filters-selected .btn{display:inline-flex;align-items:center;text-align:center;white-space:nowrap;vertical-align:middle;position:relative;justify-content:center;-webkit-user-select:none;user-select:none;border:1px solid #d8dce6;outline:0;transition:all .2s;appearance:none}.filters-selected .btn-newly-added{background-color:#009865;border:1px solid #009865}.filters-selected .btn-already-exists{background:#007771;border:1px solid #007771}.list-loader,.list-no-search{min-width:296px;min-height:35px}.loading{width:1.25rem;height:1.25rem}.empty-list-text{color:#63666a;font-size:.875rem}.btn-readonly{background:#eee;border-color:#d1d1d1!important;color:#c1c1c1!important;box-shadow:none;opacity:1}@keyframes loading{0%{transform:rotate(0)}to{transform:rotate(360deg)}}.loading{display:inline-block;width:1.25rem;height:1.25rem;vertical-align:text-bottom;border:3px solid #d8dce6;border-radius:50%;padding:0!important;position:relative}.loading:after{content:\"\";position:absolute;inset:-3px;border-radius:50%;border:3px solid transparent;border-top-color:#5e91ba;animation:loading 1s linear infinite}\n"], dependencies: [{ kind: "directive", type: i1$2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i3.NgbDropdown, selector: "[ngbDropdown]", inputs: ["autoClose", "dropdownClass", "open", "placement", "popperOptions", "container", "display"], outputs: ["openChange"], exportAs: ["ngbDropdown"] }, { kind: "directive", type: i3.NgbDropdownToggle, selector: "[ngbDropdownToggle]" }, { kind: "directive", type: i3.NgbDropdownMenu, selector: "[ngbDropdownMenu]" }, { kind: "directive", type: i3.NgbDropdownItem, selector: "[ngbDropdownItem]", inputs: ["disabled"] }, { kind: "pipe", type: i1$2.TitleCasePipe, name: "titlecase" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UserLookupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'wtw-user-lookup', template: "<div ngbDropdown container=\"body\" class=\"dropdown flex-sm-column flex-lg-column\">\r\n  <label *ngIf=\"labelText\" [for]=\"inputId\" class=\"form-control-label\">{{\r\n    labelText | titlecase\r\n  }}</label>\r\n  <div class=\"d-flex\" [ngClass]=\"{ 'flex-row': isHorizontal, 'flex-column': !isHorizontal }\">\r\n    <div class=\"d-flex flex-column\" *ngIf=\"!readOnly\">\r\n      <input\r\n        type=\"text\"\r\n        class=\"form-control dropdown-toggle dropdown\"\r\n        [class.w-100]=\"!isHorizontal\"\r\n        [class.custom-invalid]=\"isInvalid && control.touched\"\r\n        (blur)=\"onBlur()\"\r\n        (focus)=\"onFocus()\"\r\n        data-boundary=\"viewport\"\r\n        [disabled]=\"isDisabled\"\r\n        [id]=\"inputId\"\r\n        [name]=\"name\"\r\n        (input)=\"onInput($event)\"\r\n        [ngModel]=\"inputValue\"\r\n        (keyup)=\"onKeyUp($event)\"\r\n        [placeholder]=\"placeholderValue\"\r\n        [tabindex]=\"inputTabIndex\"\r\n        autocomplete=\"off\" />\r\n\r\n      <!--    The actual form field is hidden so that we can send the LookupUser object rather than just a string-->\r\n      <span ngbDropdownToggle style=\"visibility: hidden; display: block\"></span>\r\n      <input hidden [formControl]=\"control\" />\r\n\r\n      <div id=\"{{ inputId }}-userList\" ngbDropdownMenu class=\"dropdown-menu\">\r\n        <a\r\n          class=\"dropdown-item\"\r\n          *ngFor=\"let user of list; let first = first\"\r\n          ngbDropdownItem\r\n          (click)=\"selectUser(user)\"\r\n          [class.border-0]=\"first\"\r\n          >{{ user.displayName }} <span *ngIf=\"user.mail\">&nbsp;- {{ user.mail }}</span></a\r\n        >\r\n        <a\r\n          class=\"dropdown-item border-0 list-no-search\"\r\n          *ngIf=\"!loading && list?.length === 0 && (this.searchTerm?.length ?? 0) > 2\"\r\n          ngbDropdownItem>\r\n          {{ noSearchResultText }} for search term : <b>&nbsp; \"{{ this.searchTerm }}\"</b>\r\n        </a>\r\n        <a\r\n          class=\"dropdown-item border-0 list-loader\"\r\n          *ngIf=\"loading\"\r\n          ngbDropdownItem\r\n          style=\"pointer-events: none\">\r\n          <div class=\"d-flex justify-content-center align-items-center w-100\">\r\n            <div class=\"loading\"></div>\r\n          </div>\r\n        </a>\r\n      </div>\r\n    </div>\r\n\r\n    <div *ngIf=\"isMultiSelect && displayBadge\">\r\n      <ng-container\r\n        *ngIf=\"\r\n          readOnly && control.value.length === 0;\r\n          then readOnlyBlock;\r\n          else selectedUsersBlock\r\n        \"></ng-container>\r\n      <ng-template #readOnlyBlock>\r\n        <div class=\"empty-list-text\">\r\n          {{ emptyListText }}\r\n        </div>\r\n      </ng-template>\r\n      <ng-template #selectedUsersBlock>\r\n        <div\r\n          class=\"filters-selected d-sm-block d-lg-inline-block mt-sm-0\"\r\n          [ngClass]=\"{ 'mt-lg-3 py-2': !isHorizontal, 'ms-lg-3 px-2': isHorizontal }\">\r\n          <button\r\n            type=\"button\"\r\n            [disabled]=\"isDisabled || readOnly\"\r\n            (click)=\"removeUser(v)\"\r\n            *ngFor=\"let v of control.value\"\r\n            class=\"badge btn me-2 mb-2\"\r\n            ngClass=\"{{\r\n              readOnly ? 'btn-readonly' : isNewUser(v) ? 'btn-newly-added' : 'btn-already-exists'\r\n            }}\">\r\n            <span>{{ v.displayName }}</span>\r\n            <span class=\"ps-3\" *ngIf=\"!readOnly\">\u00D7</span>\r\n          </button>\r\n        </div>\r\n      </ng-template>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [".dropdown,.dropup{display:flex;flex-direction:row}.dropdown span.dropdown-toggle,.dropup span.dropdown-toggle{height:0;width:0;border:none;padding:0}.form-control{outline:none;box-shadow:0 1px 2px #0000000d;position:relative;background:#fff;border:1px solid #d8dce6;border-radius:6px;display:flex;transition:border .2s ease-in-out,background .2s ease-in-out;width:100%;font-size:.875rem;color:#3e3f42;padding:3px 10px;min-width:300px}.form-control:disabled{background:#eee;border-color:#d1d1d1;color:#c1c1c1}.form-control-label{color:#4f7ca0;font-size:.75rem;font-weight:500;letter-spacing:1px;text-transform:uppercase}.dropdown-menu{position:absolute;z-index:500;background:#fff;border:1px solid #d8dce6!important;box-shadow:0 2px 4px #0000001a;border-radius:6px;list-style:none;min-width:140px;padding:1px;margin:0;top:40px;left:0;min-height:40px;max-height:200px;overflow:auto;max-width:600px}.dropdown-menu a.dropdown-item{display:flex;align-items:center;font-size:.875rem;border-radius:0;padding:7px 15px;border-top:1px solid #fff;color:gray;cursor:pointer}.dropdown-menu a.dropdown-item:last-child{border-bottom:none}.dropdown-menu a.dropdown-item:hover,.dropdown-menu a.dropdown-item:focus,.dropdown-menu a.dropdown-item.selected{background:#f4f4fb;outline:none}.filters-selected{display:inline-block}.filters-selected .badge{letter-spacing:1px;text-transform:uppercase;font-size:10px;font-weight:500;border-radius:4px;padding:7px 7px 7px 8px;color:#fff}.filters-selected .btn{display:inline-flex;align-items:center;text-align:center;white-space:nowrap;vertical-align:middle;position:relative;justify-content:center;-webkit-user-select:none;user-select:none;border:1px solid #d8dce6;outline:0;transition:all .2s;appearance:none}.filters-selected .btn-newly-added{background-color:#009865;border:1px solid #009865}.filters-selected .btn-already-exists{background:#007771;border:1px solid #007771}.list-loader,.list-no-search{min-width:296px;min-height:35px}.loading{width:1.25rem;height:1.25rem}.empty-list-text{color:#63666a;font-size:.875rem}.btn-readonly{background:#eee;border-color:#d1d1d1!important;color:#c1c1c1!important;box-shadow:none;opacity:1}@keyframes loading{0%{transform:rotate(0)}to{transform:rotate(360deg)}}.loading{display:inline-block;width:1.25rem;height:1.25rem;vertical-align:text-bottom;border:3px solid #d8dce6;border-radius:50%;padding:0!important;position:relative}.loading:after{content:\"\";position:absolute;inset:-3px;border-radius:50%;border:3px solid transparent;border-top-color:#5e91ba;animation:loading 1s linear infinite}\n"] }]
        }], propDecorators: { isMultiSelect: [{
                type: Input
            }], displayBadge: [{
                type: Input
            }], control: [{
                type: Input
            }], inputId: [{
                type: Input
            }], placeholderValue: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], isInvalid: [{
                type: Input
            }], labelText: [{
                type: Input
            }], inputTabIndex: [{
                type: Input
            }], inputName: [{
                type: Input
            }], isHorizontal: [{
                type: Input
            }], loading: [{
                type: Input
            }], list: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], emptyListText: [{
                type: Input
            }], noSearchResultText: [{
                type: Input
            }], searchEvent: [{
                type: Output
            }], dropdown: [{
                type: ViewChild,
                args: [NgbDropdown]
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class UserLookupWrapperComponent {
    constructor(authorisationService) {
        this.authorisationService = authorisationService;
        this.isMultiSelect = true;
        this.displayBadge = true;
        this.control = new FormControl();
        this.inputId = 'userLookupId';
        this.placeholderValue = 'Search user...';
        this.isDisabled = false;
        this.isHorizontal = false;
        this.userTypes = UserTypeFlag.All;
        this.applicationName = 'wre-gateway';
        this.readOnly = false;
        this.emptyListText = 'The list Emtpy/ReadOnly';
        this.noSearchResultText = 'No user found';
        this.loading = true;
        this.searchResult = [];
    }
    searchUsers(searchTerm) {
        this.loading = true;
        this.searchResult = [];
        forkJoin([
            (this.userTypes & UserTypeFlag.InternalUsers) === UserTypeFlag.InternalUsers
                ? this.authorisationService.getUsersDetailsStartsWith(this.applicationName, UserType.InternalUsers, searchTerm)
                : of([]),
            (this.userTypes & UserTypeFlag.AdGroups) === UserTypeFlag.AdGroups
                ? this.authorisationService.getUsersDetailsStartsWith(this.applicationName, UserType.AdGroups, searchTerm)
                : of([]),
            (this.userTypes & UserTypeFlag.ExternalUsers) === UserTypeFlag.ExternalUsers
                ? this.authorisationService.getUsersDetailsStartsWith(this.applicationName, UserType.ExternalUsers, searchTerm)
                : of([]),
            (this.userTypes & UserTypeFlag.ServicePrinciples) === UserTypeFlag.ServicePrinciples
                ? this.authorisationService.getUsersDetailsStartsWith(this.applicationName, UserType.ServicePrinciples, searchTerm)
                : of([]),
        ])
            .pipe(take$1(1))
            .subscribe(([internalUsers, adGroups, externalUsers, servicePrinciples]) => {
            this.loading = false;
            this.searchResult = [
                ...internalUsers,
                ...adGroups,
                ...externalUsers,
                ...servicePrinciples,
            ].map((lookupUser) => ({
                ...lookupUser,
                displayName: lookupUser.displayName.replace('(AJ Gallagher)', '').trim(),
            }));
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UserLookupWrapperComponent, deps: [{ token: AuthorisationService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: UserLookupWrapperComponent, selector: "wtw-user-lookup-wrapper", inputs: { isMultiSelect: "isMultiSelect", displayBadge: "displayBadge", control: "control", inputId: "inputId", placeholderValue: "placeholderValue", isDisabled: "isDisabled", isInvalid: "isInvalid", labelText: "labelText", inputTabIndex: "inputTabIndex", inputName: "inputName", isHorizontal: "isHorizontal", userTypes: "userTypes", applicationName: "applicationName", readOnly: "readOnly", emptyListText: "emptyListText", noSearchResultText: "noSearchResultText" }, ngImport: i0, template: `
    <wtw-user-lookup
      [labelText]="labelText"
      [inputId]="inputId"
      [inputName]="inputName"
      [isHorizontal]="isHorizontal"
      [list]="searchResult"
      [loading]="loading"
      [control]="control"
      [placeholderValue]="placeholderValue"
      [displayBadge]="displayBadge"
      [isInvalid]="isInvalid"
      [isDisabled]="isDisabled"
      [isMultiSelect]="isMultiSelect"
      [readOnly]="readOnly"
      [noSearchResultText]="noSearchResultText"
      [emptyListText]="emptyListText"
      (searchEvent)="searchUsers($event)">
    </wtw-user-lookup>
  `, isInline: true, dependencies: [{ kind: "component", type: UserLookupComponent, selector: "wtw-user-lookup", inputs: ["isMultiSelect", "displayBadge", "control", "inputId", "placeholderValue", "isDisabled", "isInvalid", "labelText", "inputTabIndex", "inputName", "isHorizontal", "loading", "list", "readOnly", "emptyListText", "noSearchResultText"], outputs: ["searchEvent"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UserLookupWrapperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'wtw-user-lookup-wrapper', template: `
    <wtw-user-lookup
      [labelText]="labelText"
      [inputId]="inputId"
      [inputName]="inputName"
      [isHorizontal]="isHorizontal"
      [list]="searchResult"
      [loading]="loading"
      [control]="control"
      [placeholderValue]="placeholderValue"
      [displayBadge]="displayBadge"
      [isInvalid]="isInvalid"
      [isDisabled]="isDisabled"
      [isMultiSelect]="isMultiSelect"
      [readOnly]="readOnly"
      [noSearchResultText]="noSearchResultText"
      [emptyListText]="emptyListText"
      (searchEvent)="searchUsers($event)">
    </wtw-user-lookup>
  ` }]
        }], ctorParameters: function () { return [{ type: AuthorisationService }]; }, propDecorators: { isMultiSelect: [{
                type: Input
            }], displayBadge: [{
                type: Input
            }], control: [{
                type: Input
            }], inputId: [{
                type: Input
            }], placeholderValue: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], isInvalid: [{
                type: Input
            }], labelText: [{
                type: Input
            }], inputTabIndex: [{
                type: Input
            }], inputName: [{
                type: Input
            }], isHorizontal: [{
                type: Input
            }], userTypes: [{
                type: Input
            }], applicationName: [{
                type: Input
            }], readOnly: [{
                type: Input
            }], emptyListText: [{
                type: Input
            }], noSearchResultText: [{
                type: Input
            }] } });

function msalInstanceFactory(authConfigService, logger) {
    logger.logVerbose('msalInstanceFactory called');
    return new PublicClientApplication(authConfigService.msalConfig);
}
function msalInterceptorConfigFactory(authConfigService, logger) {
    logger.logVerbose('msalInterceptorConfigFactory called');
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: authConfigService.protectedResourceMap,
    };
}
function msalGuardConfigFactory(logger) {
    logger.logVerbose('msalGuardConfigFactory called');
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [],
        },
    };
}

class WreAuthlibModule {
    constructor(eventService) {
        eventService.initialize();
    }
    static forRoot(internalConfig, externalConfig, loggingEnabled, logLevel) {
        return {
            ngModule: WreAuthlibModule,
            providers: [
                {
                    provide: INTERNAL_AUTH_CONFIG,
                    useValue: internalConfig,
                },
                {
                    provide: EXTERNAL_AUTH_CONFIG,
                    useValue: externalConfig,
                },
                {
                    provide: LOGGING_ENABLED,
                    useValue: loggingEnabled ?? false,
                },
                {
                    provide: LOG_LEVEL,
                    useValue: logLevel ?? LogLevel.Info,
                },
                {
                    provide: MSAL_INSTANCE,
                    useFactory: msalInstanceFactory,
                    deps: [AuthConfigService, LoggerService],
                },
                {
                    provide: MSAL_GUARD_CONFIG,
                    useFactory: msalGuardConfigFactory,
                    deps: [LoggerService],
                },
                {
                    provide: MSAL_INTERCEPTOR_CONFIG,
                    useFactory: msalInterceptorConfigFactory,
                    deps: [AuthConfigService, LoggerService],
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: MsalInterceptor,
                    multi: true,
                },
                MsalService,
                MsalGuard,
                MsalBroadcastService,
            ],
        };
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WreAuthlibModule, deps: [{ token: EventService }], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: WreAuthlibModule, declarations: [UserLookupComponent, UserLookupWrapperComponent], imports: [CommonModule,
            HttpClientModule,
            ReactiveFormsModule,
            FormsModule,
            NgbModule,
            MsalModule], exports: [UserLookupWrapperComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WreAuthlibModule, imports: [CommonModule,
            HttpClientModule,
            ReactiveFormsModule,
            FormsModule,
            NgbModule,
            MsalModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WreAuthlibModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        HttpClientModule,
                        ReactiveFormsModule,
                        FormsModule,
                        NgbModule,
                        MsalModule,
                    ],
                    declarations: [UserLookupComponent, UserLookupWrapperComponent],
                    exports: [UserLookupWrapperComponent],
                }]
        }], ctorParameters: function () { return [{ type: EventService }]; } });

/*
 * Public API Surface of wre-authlib
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AuthenticationService, AuthorisationService, CacheService, MsalConfigGuard, MsalLoginGuard, PermissionGuard, PreLoginComponent, RoleGuard, UnauthenticatedGuard, UserLookupWrapperComponent, UserProfile, UserType, UserTypeFlag, WreAuthlibModule };
